import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { getUserFacingError } from '../utils/errors';
import { rememberPostVerifyPath } from '../utils/emailVerification';
import MarketingSurface from '../components/MarketingSurface';
import { marketingColor, marketingFont } from '../theme/marketingTokens';

export const RegisterPage: React.FC = () => {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [searchParams] = useSearchParams();
  // The public vendor sign-up sends reps here with their intent. Without this the
  // form defaults to a store account and the claim they came to make is lost.
  const claimIntent = searchParams.get('intent') === 'claim-brand';
  const claimBrand = searchParams.get('brand') || '';
  // From an emailed brand rep invitation; carried through to vendor onboarding, which checks it.
  const inviteToken = searchParams.get('invite') || '';
  const [userType, setUserType] = useState<'store' | 'vendor_supplier'>(
    claimIntent ? 'vendor_supplier' : 'store'
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  // A brand-new account created from the invitation accept page is joining an
  // existing organization, not creating one — send it straight back to the
  // invite instead of into org-creation onboarding.
  const redirect = searchParams.get('redirect');
  const isInvitationRedirect = Boolean(redirect && redirect.startsWith('/invitations/accept'));

  const onboardingPath = (type: 'store' | 'vendor_supplier') => {
    if (isInvitationRedirect && redirect) return redirect;
    // Always start at terms. Skipping it used to let a store finish setup,
    // get bounced back to terms, and create a second organization.
    const params = new URLSearchParams();
    if (type === 'vendor_supplier' && claimBrand) {
      params.set('brand', claimBrand);
    }
    if (type === 'vendor_supplier' && inviteToken) {
      params.set('invite', inviteToken);
    }
    const query = params.toString();
    return query ? `/onboarding/terms?${query}` : '/onboarding/terms';
  };

  const goToOnboarding = (type: 'store' | 'vendor_supplier') => {
    navigate(onboardingPath(type));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, displayName, userType);
      rememberPostVerifyPath(onboardingPath(userType));
      navigate('/verify-email');
    } catch (err) {
      setError(getUserFacingError(err, 'Failed to create account. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError('');
    setLoading(true);

    try {
      await loginWithGoogle({ userType });
      await new Promise((resolve) => setTimeout(resolve, 500));
      goToOnboarding(userType);
    } catch (err) {
      setError(getUserFacingError(err, 'Failed to sign up with Google. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <MarketingSurface>
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper sx={{ padding: 4, width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img
                  src="/logo-white.svg"
                  alt="Motzu Labs"
                  style={{
                    height: '28px',
                    width: 'auto',
                    maxWidth: '160px',
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `<span style="font-family: ${marketingFont.display}; font-weight: 700; font-size: 1.25rem; color: ${marketingColor.text};">Motzu Labs</span>`;
                    }
                  }}
                />
              </Box>
              <Typography component="h1" variant="h5" gutterBottom>
                Sign Up
              </Typography>
              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="displayName"
                  label="Full Name"
                  name="displayName"
                  autoComplete="name"
                  autoFocus
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />

                <FormControl component="fieldset" sx={{ mt: 3, mb: 2 }}>
                  <FormLabel component="legend">Account Type</FormLabel>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose how you'll be using the platform
                  </Typography>
                  <RadioGroup
                    value={userType}
                    onChange={(e) => setUserType(e.target.value as 'store' | 'vendor_supplier')}
                    sx={{ gap: 2 }}
                  >
                    <Card variant="outlined" sx={{ p: 0 }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <FormControlLabel
                          value="store"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="medium">
                                Store Owner
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                I run a camera store and want to manage inventory, pricing, and customer data
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start', m: 0 }}
                        />
                      </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ p: 0 }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <FormControlLabel
                          value="vendor_supplier"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="medium">
                                Vendor/Supplier
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                I'm a camera manufacturer or distributor and want to manage product feeds and MAP pricing
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start', m: 0 }}
                        />
                      </CardContent>
                    </Card>
                  </RadioGroup>
                </FormControl>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Sign Up'}
                </Button>

                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    OR
                  </Typography>
                </Divider>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<GoogleIcon />}
                  onClick={handleGoogleSignUp}
                  disabled={loading}
                  sx={{ mb: 2 }}
                >
                  Sign up with Google
                </Button>

                <Box textAlign="center">
                  <Link component={RouterLink} to="/login" variant="body2">
                    Already have an account? Sign In
                  </Link>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </MarketingSurface>
  );
};
